import { Injectable } from '@angular/core';
import { NgForm } from '@angular/forms';

import * as _ from 'lodash';

import { ProcessErrorCode } from 'src/app/ajs-upgraded-providers';
import { ApiUtilsService } from 'src/app/api/services/api-utils.service';
import { CompanyApiService } from 'src/app/api/services/company-api.service';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { UserAuthService } from 'src/app/auth/services/user-auth.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import { TrackerService } from 'src/app/components/logging/tracker.service';
import { ModalService } from 'src/app/components/modals/modal.service';
import { AddressService } from 'src/app/shared/services/address.service';
import {
  ClientCredentials,
  OAuthService,
} from 'src/app/components/content-oauth/services/oauth.service';
import { Company } from 'src/app/api/services/company';

@Injectable({
  providedIn: 'root',
})
export class CompanyService {
  loading = false;
  company: any = {};
  apiError: string;
  formError: string;
  isAddressError = false;
  readonly companyCredentialProviders = [
    {
      name: 'domo',
      enabledKey: 'domoEnabled',
      clientIdKey: 'domoClientId',
      clientSecretKey: 'domoClientSecret',
    },
    {
      name: 'adastra',
      enabledKey: 'adastraEnabled',
      clientIdKey: 'adastraClientId',
      clientSecretKey: 'adastraClientSecret',
    },
  ];

  constructor(
    private userStateService: UserStateService,
    private companyStateService: CompanyStateService,
    private userAuthService: UserAuthService,
    private companyApiService: CompanyApiService,
    private addressFactory: AddressService,
    private tracker: TrackerService,
    private processErrorCode: ProcessErrorCode,
    private modalService: ModalService,
    private apiUtils: ApiUtilsService,
    private oAuthService: OAuthService,
  ) {
    this._clearErrorMessages();
  }

  private _verifyAdmin(company: any) {
    if (!this.userStateService.isRiseStoreAdmin()) {
      delete company.shareCompanyPlan;
      delete company.shareUnlimitedPlan;
      delete company.unlockAllFeatures;
    }
  }

  private _clearErrorMessages() {
    this.formError = null;
    this.apiError = null;
    this.isAddressError = false;
  }

  _showErrorMessage(action, error) {
    this.formError = 'Failed to ' + action + ' Company.';
    this.apiError = this.processErrorCode(error);
  }

  async getCompany(companyId: string) {
    if (companyId) {
      this.loading = true;
      try {
        const company = await this.companyApiService.getCompany(companyId);

        // Fetch credentials for all providers and add to settings
        await this.loadCompanyCredentials(company);

        this.company = this.normalizeCompany(company);
        this.company.isChargebee = company && company.origin === 'Chargebee';
      } catch (resp) {
        this._showErrorMessage('load', resp);
      } finally {
        this.loading = false;
      }
    }
  }

  private async loadCompanyCredentials(company: Company): Promise<void> {
    if (!company.settings) {
      company.settings = {};
    }

    for (const provider of this.companyCredentialProviders) {
      try {
        const credentials = await this.oAuthService.getCompanyCredentials(
          company.id,
          provider.name,
        );

        if (credentials) {
          // Add enabled flag and credentials to settings
          (company.settings as any)[provider.enabledKey] = true;
          company.settings[provider.clientIdKey] = credentials.clientId;
          company.settings[provider.clientSecretKey] = credentials.clientSecret;
        } else {
          // No credentials found, set enabled to false
          (company.settings as any)[provider.enabledKey] = false;
        }
      } catch (error) {
        console.error(
          `Failed to load credentials for provider ${provider.name}:`,
          error,
        );
        // Set enabled to false if there's an error loading credentials
        (company.settings as any)[provider.enabledKey] = false;
      }
    }
  }

  /**
   * Clears client ID and client secret from the in-memory company for
   * integrations that are disabled. Call after a successful save so disabled
   * integrations' credentials are not kept in memory.
   */
  clearCompanyCredentials(): void {
    if (!this.company.settings) {
      return;
    }
    this.companyCredentialProviders.forEach((provider) => {
      const enabled = this.company.settings?.[provider.enabledKey];
      if (!this.isTrue(enabled)) {
        this.company.settings[provider.clientIdKey] = undefined;
        this.company.settings[provider.clientSecretKey] = undefined;
      }
    });
  }

  private isTrue = (value: string | boolean) =>
    value === true || value === 'true';

  private normalizeCompany(company: any) {
    return {
      ...company,
      settings: {
        ...company.settings,
        allowCompanyRegistrations: this.isTrue(
          (company.settings || {}).allowCompanyRegistrations,
        ),
        freeForURLsDisplayEnforced: this.isTrue(
          (company.settings || {}).freeForURLsDisplayEnforced,
        ),
        jamfEnabled: this.isTrue((company.settings || {}).jamfEnabled),
        mailSyncEnabled: this.isTrue((company.settings || {}).mailSyncEnabled),
        onboardingCompleted: this.isTrue(
          (company.settings || {}).onboardingCompleted,
        ),
        samlSso: this.isTrue((company.settings || {}).samlSso),
        ssoEnabled: this.isTrue((company.settings || {}).ssoEnabled),
        ssoRequired: this.isTrue((company.settings || {}).ssoRequired),
        ssoUserProvisioningEnabled: this.isTrue(
          (company.settings || {}).ssoUserProvisioningEnabled,
        ),
        fuseEnabled: this.isTrue((company.settings || {}).fuseEnabled),
        mosyleEnabled: this.isTrue((company.settings || {}).mosyleEnabled),
        domoEnabled: this.isTrue((company.settings || {}).domoEnabled),
        adastraEnabled: this.isTrue((company.settings || {}).adastraEnabled),
      },
    };
  }

  async save(companyForm: NgForm, oldCompany: any) {
    this._clearErrorMessages();

    this.loading = true;

    try {
      try {
        await this.addressFactory.validateAddressIfChanged(
          companyForm.form.controls,
          this.company,
        );
      } catch (error) {
        this.formError = "We couldn't update your address.";
        this.apiError = this.processErrorCode(error);
        this.isAddressError = true;
        throw error;
      }

      const companyCopy = _.cloneDeep(this.company);

      this._verifyAdmin(companyCopy);

      try {
        await this.updateOTPCompanyCredentials(companyCopy, oldCompany);
        await this.companyApiService.updateCompany(
          this.company.id,
          companyCopy,
        );

        this.tracker.companyEvent(
          'Company Updated',
          this.companyStateService.getSelectedCompanyId(),
          this.companyStateService.getSelectedCompanyName(),
          !this.companyStateService.isSubcompanySelected(),
        );

        this.companyStateService.updateCompanySettings(this.company);
      } catch (error) {
        this._showErrorMessage('update', error);
        throw error;
      }
    } finally {
      this.loading = false;
    }
  }

  private async updateOTPCompanyCredentials(
    companyCopy: Company,
    oldCompany: Company,
  ): Promise<void> {
    for (const provider of this.companyCredentialProviders) {
      const enabled = this.isTrue(companyCopy.settings?.[provider.enabledKey]);
      const clientId = companyCopy.settings?.[provider.clientIdKey];
      const clientSecret = companyCopy.settings?.[provider.clientSecretKey];

      // Remove credentials from company settings
      if (companyCopy.settings) {
        delete companyCopy.settings[provider.clientIdKey];
        delete companyCopy.settings[provider.clientSecretKey];
      }

      const credentialsChanged: { clientId?: string; clientSecret?: string } =
        {};
      if (clientId !== oldCompany.settings?.[provider.clientIdKey]) {
        credentialsChanged.clientId = clientId;
      }
      if (
        clientSecret !== '********' &&
        clientSecret !== oldCompany.settings?.[provider.clientSecretKey]
      ) {
        credentialsChanged.clientSecret = clientSecret;
      }

      if (
        (enabled !== undefined &&
          enabled !==
            this.isTrue(oldCompany.settings?.[provider.enabledKey])) ||
        credentialsChanged.clientId ||
        credentialsChanged.clientSecret
      ) {
        await this.updateOTPCompanyCredentialsForProvider(
          provider.name,
          enabled,
          credentialsChanged,
        );
      }
    }
  }

  private async updateOTPCompanyCredentialsForProvider(
    provider: string,
    enabled: boolean,
    credentials: Partial<ClientCredentials>,
  ): Promise<void> {
    if (enabled) {
      // Try POST first to create credentials
      try {
        if (credentials.clientId && credentials.clientSecret) {
          await this.oAuthService.createCompanyCredentials(
            this.company.id,
            provider,
            credentials as ClientCredentials,
          );
        } else {
          throw { error: { code: 'CREDENTIALS_ALREADY_EXIST' } };
        }
      } catch (error: any) {
        // If credentials already exist, update with PUT
        if (error?.error?.code === 'CREDENTIALS_ALREADY_EXIST') {
          await this.oAuthService.updateCompanyCredentials(
            this.company.id,
            provider,
            credentials,
          );
        } else {
          throw error;
        }
      }
    } else {
      // Delete credentials when setting is not enabled
      try {
        await this.oAuthService.deleteCompanyCredentials(
          this.company.id,
          provider,
        );
      } catch (error: any) {
        // Ignore if credentials don't exist
        if (
          error?.status !== 404 &&
          error?.error?.code !== 'CREDENTIALS_NOT_FOUND'
        ) {
          throw error;
        }
      }
    }
  }

  deleteCompany(company: any) {
    this.loading = true;

    return this.companyApiService
      .deleteCompany(company.id)
      .then(() => {
        this.tracker.companyEvent(
          'Company Deleted',
          this.companyStateService.getSelectedCompanyId(),
          this.companyStateService.getSelectedCompanyName(),
          !this.companyStateService.isSubcompanySelected(),
        );

        if (this.companyStateService.getUserCompanyId() === company.id) {
          this.userAuthService.signOut();
        } else if (
          this.companyStateService.getSelectedCompanyId() === company.id
        ) {
          this.companyStateService.resetCompany();
        }
      })
      .catch((error) => {
        if (
          this.apiUtils.getError(error).message ===
          'Company has active subscriptions and cannot be deleted.'
        ) {
          const message1 =
            'You have an active subscription and cannot delete your company.';
          const message2 =
            'Please contact <a href="mailto:help@risevision.com">help@risevision.com</a> for assistance.';

          this.modalService.showMessage(
            'You have an active subscription.',
            `${message1} ${message2}`,
          );
        } else {
          this._showErrorMessage('delete', error);
        }
      })
      .finally(() => {
        this.loading = false;
      });
  }

  resetCompanyField(type) {
    this.loading = true;

    return this.companyApiService
      .regenerateField(this.company.id, type)
      .catch((error) => {
        this.apiError = this.processErrorCode(error);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  exportCompany(company?: any) {
    return this.modalService
      .confirm(
        'Export companies?',
        'An export file will be prepared and emailed to you at <b>' +
          this.userStateService.getUserEmail() +
          '</b> once ready.<br/> Please ensure your email is configured to accept emails from <b>no-reply@risevision.com</b>.',
        'Export',
        'Cancel',
      )
      .then(() => {
        this.loading = true;
        return this.companyApiService
          .exportCompany(company ? company.id : this.company.id)
          .catch((error) => {
            if (error) {
              this._showErrorMessage('export', error);
            }
          })
          .finally(() => {
            this.loading = false;
          });
      });
  }
}
